import React, { useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  SelectionMode,
  Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useDialogueStore } from '../store/dialogueStore';
import { DialogueNode } from './nodes/DialogueNode';
import { ConditionNode } from './nodes/ConditionNode';
import { ResponseNode } from './nodes/ResponseNode';
import { CustomEdge } from './edges/CustomEdge';
import { AnimatedEdge } from './edges/AnimatedEdge';

const nodeTypes = {
  dialogue: DialogueNode,
  condition: ConditionNode,
  response: ResponseNode,
};

const edgeTypes = {
  custom: CustomEdge,
  animated: AnimatedEdge,
};

const GraphEditor: React.FC = () => {
  const { nodes: storeNodes, edges: storeEdges, setNodes, setEdges, setSelectedNode } = useDialogueStore();
  const [nodes, setNodesState, onNodesChange] = useNodesState(storeNodes);
  const [edges, setEdgesState, onEdgesChange] = useEdgesState(storeEdges);

  useEffect(() => {
    setNodesState(storeNodes);
  }, [storeNodes, setNodesState]);

  useEffect(() => {
    setEdgesState(storeEdges);
  }, [storeEdges, setEdgesState]);

  const onConnect = useCallback(
    (params: Connection | Edge) => setEdgesState((eds) => addEdge(params, eds)),
    [setEdgesState]
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      setSelectedNode(node.id);
    },
    [setSelectedNode]
  );

  const onNodesChangeHandler = useCallback(
    (changes: any) => {
      onNodesChange(changes);
      // Note: This might cause issues - nodes state might be stale
      // TODO: Review this logic
      setNodes(nodes);
    },
    [nodes, onNodesChange, setNodes]
  );

  const onEdgesChangeHandler = useCallback(
    (changes: any) => {
      onEdgesChange(changes);
      // Note: This might cause issues - edges state might be stale
      // TODO: Review this logic
      setEdges(edges);
    },
    [edges, onEdgesChange, setEdges]
  );

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChangeHandler}
        onEdgesChange={onEdgesChangeHandler}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        selectionMode={SelectionMode.Partial}
        panOnScroll
        selectionOnDrag
        fitView
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <Controls />
        <MiniMap 
          nodeStrokeColor={(n) => {
            if (n.type === 'dialogue') return '#1e3a8a';
            if (n.type === 'condition') return '#059669';
            if (n.type === 'response') return n.data.isDefault ? '#dc2626' : '#7c3aed';
            return '#999';
          }}
          nodeColor={(n) => {
            if (n.type === 'dialogue') return '#3b82f6';
            if (n.type === 'condition') return '#10b981';
            if (n.type === 'response') return n.data.isDefault ? '#ef4444' : '#8b5cf6';
            return '#ddd';
          }}
          nodeBorderRadius={2}
        />
        {nodes.length === 0 && (
          <Panel position="top-center" className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mt-4">
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-2">No dialogue loaded</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Import dialogue JSON or load an example to get started
              </p>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
};

export default function GraphEditorWithProvider() {
  return (
    <ReactFlowProvider>
      <GraphEditor />
    </ReactFlowProvider>
  );
}